import React, { useState, useEffect, useRef } from 'react';
import {
    TrendingUp, TrendingDown, Minus, DollarSign, Target,
    BarChart3, ArrowUpRight, ArrowDownRight, Activity,
    ShoppingCart, RefreshCw, ChevronUp, Clock, X, Maximize2, Image as ImageIcon
} from 'lucide-react';
import { formatNumber, formatCurrency, formatPercent, calculateChange, formatDate } from '../utils/helpers';
import CreativesQuickView from './CreativesQuickView';

const CampaignDetailRow = ({ campaign, dateRange, userId, brandId, onCollapse }) => {
    const [loading, setLoading] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [activeTab, setActiveTab] = useState('trends');
    const [zoomedChart, setZoomedChart] = useState(null);

    useEffect(() => {
        if (campaign) {
            fetchCampaignDetails();
        }
    }, [campaign, dateRange]);

    const fetchCampaignDetails = async () => {
        if (!campaign) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                userId,
                brandId,
                campaignId: campaign.platformCampaignId,
                platform: campaign.platform,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            });

            const response = await fetch(`http://localhost:3001/api/campaigns/detail?${params}`);

            if (!response.ok) {
                throw new Error('Failed to fetch campaign details');
            }

            const data = await response.json();
            setDetailData(data);
        } catch (error) {
            console.error('Error fetching campaign details:', error);
            setDetailData({
                campaign,
                currentPeriod: campaign.currentMetrics || {},
                previousPeriod: {},
                dailyData: [],
                insights: []
            });
        } finally {
            setLoading(false);
        }
    };

    const current = detailData?.currentPeriod || campaign?.currentMetrics || {};
    const previous = detailData?.previousPeriod || {};
    const dailyData = detailData?.dailyData || [];
    const periodInfo = detailData?.periodInfo || {};

    const changes = {
        conversions: calculateChange(current.conversions || 0, previous.conversions || 0),
        cpc: calculateChange(current.cpc || 0, previous.cpc || 0),
        cpm: calculateChange(current.cpm || 0, previous.cpm || 0),
        costPerConversion: calculateChange(current.costPerConversion || 0, previous.costPerConversion || 0)
    };

    const ChangeIndicator = ({ value, inverted = false }) => {
        if (value === 0 || isNaN(value) || !isFinite(value)) {
            return <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>--</span>;
        }

        const isPositive = inverted ? value < 0 : value > 0;
        const Icon = isPositive ? TrendingUp : TrendingDown;
        const color = isPositive ? 'var(--color-success)' : 'var(--color-error)';

        return (
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                color,
                fontSize: 'var(--font-size-xs)'
            }}>
                <Icon size={12} />
                {Math.abs(value).toFixed(1)}%
            </span>
        );
    };

    const MetricCard = ({ icon: Icon, label, value, change, inverted = false, prevValue }) => (
        <div style={{
            padding: 'var(--space-3)',
            background: 'var(--color-bg-primary)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            minWidth: '140px'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                <Icon size={14} style={{ color: 'var(--color-text-secondary)' }} />
                <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>{label}</span>
            </div>
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                {value}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <ChangeIndicator value={change} inverted={inverted} />
                {prevValue && (
                    <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                        was {prevValue}
                    </span>
                )}
            </div>
        </div>
    );

    // Interactive Chart Component with tooltips
    const InteractiveChart = ({ data, dataKey, color, format, label, height = 50, width = 180, isZoomed = false }) => {
        const [hoveredPoint, setHoveredPoint] = useState(null);
        const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
        const svgRef = useRef(null);

        if (!data || data.length === 0) return null;

        const values = data.map(d => d[dataKey] || 0);
        const max = Math.max(...values, 0.01);
        const min = Math.min(...values, 0);
        const range = max - min || 1;

        const padding = isZoomed ? 40 : 4;
        const chartWidth = width - (isZoomed ? 60 : 0);
        const chartHeight = height - (isZoomed ? 40 : 0);

        const points = values.map((v, i) => ({
            x: padding + (i / (values.length - 1 || 1)) * (chartWidth - 2 * padding),
            y: (isZoomed ? 20 : 0) + chartHeight - padding - ((v - min) / range) * (chartHeight - 2 * padding),
            value: v,
            date: data[i].date,
            index: i
        }));

        const pathD = points.length > 0
            ? `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`
            : '';

        const areaD = points.length > 0
            ? `${pathD} L ${points[points.length - 1].x},${(isZoomed ? 20 : 0) + chartHeight - padding} L ${padding},${(isZoomed ? 20 : 0) + chartHeight - padding} Z`
            : '';

        const formatValue = (val) => {
            if (format === 'currency') return formatCurrency(val);
            if (format === 'percent') return formatPercent(val);
            if (format === 'roas') return `${val.toFixed(2)}x`;
            return formatNumber(val);
        };

        const handleMouseMove = (e) => {
            if (!svgRef.current) return;
            const rect = svgRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;

            // Find closest point
            let closest = points[0];
            let closestDist = Math.abs(x - closest.x);
            for (const point of points) {
                const dist = Math.abs(x - point.x);
                if (dist < closestDist) {
                    closestDist = dist;
                    closest = point;
                }
            }

            setHoveredPoint(closest);
            setTooltipPos({ x: closest.x, y: closest.y });
        };

        const handleMouseLeave = () => {
            setHoveredPoint(null);
        };

        // Generate Y-axis labels for zoomed view
        const yAxisLabels = isZoomed ? [
            { value: max, y: 20 + padding },
            { value: (max + min) / 2, y: 20 + chartHeight / 2 },
            { value: min, y: 20 + chartHeight - padding }
        ] : [];

        // Generate X-axis labels for zoomed view (show every nth label)
        const xAxisLabels = isZoomed && data.length > 0 ? data.filter((_, i) => {
            const step = Math.ceil(data.length / 6);
            return i % step === 0 || i === data.length - 1;
        }).map((d, i, arr) => {
            const origIndex = data.findIndex(item => item.date === d.date);
            return {
                label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                x: points[origIndex]?.x || 0
            };
        }) : [];

        return (
            <svg
                ref={svgRef}
                width={width}
                height={height}
                style={{ display: 'block', cursor: isZoomed ? 'crosshair' : 'pointer' }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            >
                <defs>
                    <linearGradient id={`gradient-${dataKey}-${isZoomed ? 'zoom' : 'mini'}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                        <stop offset="100%" stopColor={color} stopOpacity="0.05" />
                    </linearGradient>
                </defs>

                {/* Y-axis labels for zoomed view */}
                {yAxisLabels.map((item, i) => (
                    <text
                        key={i}
                        x={35}
                        y={item.y + 4}
                        fontSize="10"
                        fill="var(--color-text-secondary)"
                        textAnchor="end"
                    >
                        {formatValue(item.value)}
                    </text>
                ))}

                {/* X-axis labels for zoomed view */}
                {xAxisLabels.map((item, i) => (
                    <text
                        key={i}
                        x={item.x}
                        y={height - 5}
                        fontSize="10"
                        fill="var(--color-text-secondary)"
                        textAnchor="middle"
                    >
                        {item.label}
                    </text>
                ))}

                {/* Grid lines for zoomed view */}
                {isZoomed && (
                    <>
                        <line x1={padding} y1={20 + padding} x2={chartWidth} y2={20 + padding} stroke="var(--color-border)" strokeDasharray="2,2" />
                        <line x1={padding} y1={20 + chartHeight / 2} x2={chartWidth} y2={20 + chartHeight / 2} stroke="var(--color-border)" strokeDasharray="2,2" />
                        <line x1={padding} y1={20 + chartHeight - padding} x2={chartWidth} y2={20 + chartHeight - padding} stroke="var(--color-border)" strokeDasharray="2,2" />
                    </>
                )}

                {/* Area fill */}
                <path d={areaD} fill={`url(#gradient-${dataKey}-${isZoomed ? 'zoom' : 'mini'})`} />

                {/* Line */}
                <path
                    d={pathD}
                    fill="none"
                    stroke={color}
                    strokeWidth={isZoomed ? 2.5 : 2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* Data points for zoomed view */}
                {isZoomed && points.map((point, i) => (
                    <circle
                        key={i}
                        cx={point.x}
                        cy={point.y}
                        r={hoveredPoint?.index === i ? 6 : 3}
                        fill={hoveredPoint?.index === i ? color : 'var(--color-bg-primary)'}
                        stroke={color}
                        strokeWidth={2}
                        style={{ transition: 'r 0.1s' }}
                    />
                ))}

                {/* Hover indicator line */}
                {hoveredPoint && (
                    <>
                        <line
                            x1={tooltipPos.x}
                            y1={isZoomed ? 20 : 0}
                            x2={tooltipPos.x}
                            y2={isZoomed ? height - 25 : height}
                            stroke={color}
                            strokeWidth={1}
                            strokeDasharray="3,3"
                            opacity={0.5}
                        />
                        <circle
                            cx={tooltipPos.x}
                            cy={tooltipPos.y}
                            r={isZoomed ? 6 : 4}
                            fill={color}
                            stroke="var(--color-bg-primary)"
                            strokeWidth={2}
                        />
                    </>
                )}

                {/* Tooltip */}
                {hoveredPoint && (
                    <g>
                        <rect
                            x={Math.min(tooltipPos.x - 45, width - 95)}
                            y={Math.max(tooltipPos.y - 45, 5)}
                            width={90}
                            height={isZoomed ? 38 : 32}
                            rx={4}
                            fill="var(--color-bg-primary)"
                            stroke="var(--color-border)"
                            strokeWidth={1}
                            filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                        />
                        <text
                            x={Math.min(tooltipPos.x, width - 50)}
                            y={Math.max(tooltipPos.y - 28, 20)}
                            fontSize="11"
                            fontWeight="600"
                            fill="var(--color-text-primary)"
                            textAnchor="middle"
                        >
                            {formatValue(hoveredPoint.value)}
                        </text>
                        <text
                            x={Math.min(tooltipPos.x, width - 50)}
                            y={Math.max(tooltipPos.y - 14, 34)}
                            fontSize="10"
                            fill="var(--color-text-secondary)"
                            textAnchor="middle"
                        >
                            {new Date(hoveredPoint.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </text>
                    </g>
                )}
            </svg>
        );
    };

    // Zoomed Chart Modal
    const ChartModal = ({ chart, onClose }) => {
        if (!chart) return null;

        const firstValue = dailyData[0]?.[chart.dataKey] || 0;
        const lastValue = dailyData[dailyData.length - 1]?.[chart.dataKey] || 0;
        const trendChange = calculateChange(lastValue, firstValue);
        const isInverted = ['cpc', 'cpm', 'costPerConversion'].includes(chart.dataKey);

        const formatValue = (val) => {
            if (chart.format === 'currency') return formatCurrency(val);
            if (chart.format === 'percent') return formatPercent(val);
            if (chart.format === 'roas') return `${val.toFixed(2)}x`;
            return formatNumber(val);
        };

        // Calculate stats
        const values = dailyData.map(d => d[chart.dataKey] || 0);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        const maxVal = Math.max(...values);
        const minVal = Math.min(...values);

        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}
                onClick={onClose}
            >
                <div
                    style={{
                        background: 'var(--color-bg-primary)',
                        borderRadius: 'var(--radius-xl)',
                        padding: 'var(--space-6)',
                        width: '800px',
                        maxWidth: '90vw',
                        maxHeight: '80vh',
                        overflow: 'auto'
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <div>
                            <h3 style={{ margin: 0, marginBottom: 'var(--space-1)' }}>{chart.label}</h3>
                            <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                {dateRange.startDate} to {dateRange.endDate}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 'var(--space-2)',
                                color: 'var(--color-text-secondary)'
                            }}
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Stats Row */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 'var(--space-4)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <div style={{
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)'
                        }}>
                            <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-1)' }}>
                                Current
                            </div>
                            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                                {formatValue(lastValue)}
                            </div>
                        </div>
                        <div style={{
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)'
                        }}>
                            <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-1)' }}>
                                Average
                            </div>
                            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                                {formatValue(avg)}
                            </div>
                        </div>
                        <div style={{
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)'
                        }}>
                            <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-1)' }}>
                                High / Low
                            </div>
                            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                                {formatValue(maxVal)} / {formatValue(minVal)}
                            </div>
                        </div>
                        <div style={{
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)'
                        }}>
                            <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-1)' }}>
                                Trend
                            </div>
                            <div style={{
                                fontSize: 'var(--font-size-lg)',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)'
                            }}>
                                <ChangeIndicator value={trendChange} inverted={isInverted} />
                            </div>
                        </div>
                    </div>

                    {/* Large Chart */}
                    <div style={{
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--space-4)'
                    }}>
                        <InteractiveChart
                            data={dailyData}
                            dataKey={chart.dataKey}
                            color={chart.color}
                            format={chart.format}
                            label={chart.label}
                            height={300}
                            width={720}
                            isZoomed={true}
                        />
                    </div>

                    {/* Data Table */}
                    <div style={{ marginTop: 'var(--space-4)' }}>
                        <h4 style={{ marginBottom: 'var(--space-2)' }}>Daily Data</h4>
                        <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                        <th style={{ padding: 'var(--space-2)', textAlign: 'left' }}>Date</th>
                                        <th style={{ padding: 'var(--space-2)', textAlign: 'right' }}>{chart.label}</th>
                                        <th style={{ padding: 'var(--space-2)', textAlign: 'right' }}>Change</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...dailyData].reverse().map((day, i, arr) => {
                                        const prevDay = arr[i + 1];
                                        const change = prevDay ? calculateChange(day[chart.dataKey], prevDay[chart.dataKey]) : 0;
                                        return (
                                            <tr key={day.date} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                                <td style={{ padding: 'var(--space-2)' }}>
                                                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                </td>
                                                <td style={{ padding: 'var(--space-2)', textAlign: 'right', fontWeight: 500 }}>
                                                    {formatValue(day[chart.dataKey] || 0)}
                                                </td>
                                                <td style={{ padding: 'var(--space-2)', textAlign: 'right' }}>
                                                    <ChangeIndicator value={change} inverted={isInverted} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const TrendCard = ({ label, dataKey, format, color, currentValue }) => {
        const formattedCurrent = format === 'currency'
            ? formatCurrency(currentValue || 0)
            : format === 'percent'
            ? formatPercent(currentValue || 0)
            : format === 'roas'
            ? `${(currentValue || 0).toFixed(2)}x`
            : formatNumber(currentValue || 0);

        const firstValue = dailyData[0]?.[dataKey] || 0;
        const lastValue = dailyData[dailyData.length - 1]?.[dataKey] || 0;
        const trendChange = calculateChange(lastValue, firstValue);

        return (
            <div
                style={{
                    padding: 'var(--space-3)',
                    background: 'var(--color-bg-primary)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setZoomedChart({ label, dataKey, format, color });
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = color;
                    e.currentTarget.style.boxShadow = `0 0 0 1px ${color}40`;
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-2)'
                }}>
                    <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                        {label}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span style={{ fontWeight: 600 }}>{formattedCurrent}</span>
                        {dailyData.length > 1 && (
                            <ChangeIndicator
                                value={trendChange}
                                inverted={dataKey === 'cpc' || dataKey === 'cpm' || dataKey === 'costPerConversion'}
                            />
                        )}
                    </div>
                </div>
                <InteractiveChart
                    data={dailyData}
                    dataKey={dataKey}
                    color={color}
                    format={format}
                    label={label}
                />
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 'var(--space-1)'
                }}>
                    <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {dailyData.length} days
                    </span>
                    <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <Maximize2 size={10} /> Click to expand
                    </span>
                </div>
            </div>
        );
    };

    const generateInsights = () => {
        const insights = [];

        if (current.roas >= 3) {
            insights.push({ type: 'success', text: `Strong ROAS of ${current.roas.toFixed(2)}x` });
        } else if (current.roas > 0 && current.roas < 1) {
            insights.push({ type: 'danger', text: 'ROAS below break-even - consider optimization' });
        }

        if (current.ctr >= 2) {
            insights.push({ type: 'success', text: `High engagement with ${formatPercent(current.ctr)} CTR` });
        } else if (current.ctr > 0 && current.ctr < 0.5) {
            insights.push({ type: 'warning', text: 'Low CTR - test new creative or targeting' });
        }

        if (previous.roas && current.roas) {
            const roasChange = calculateChange(current.roas, previous.roas);
            if (roasChange > 10) {
                insights.push({ type: 'success', text: `ROAS improved ${roasChange.toFixed(0)}% vs previous period` });
            } else if (roasChange < -10) {
                insights.push({ type: 'warning', text: `ROAS declined ${Math.abs(roasChange).toFixed(0)}% vs previous period` });
            }
        }

        if (previous.spend && current.spend) {
            const spendChange = calculateChange(current.spend, previous.spend);
            const revenueChange = calculateChange(current.revenue || 0, previous.revenue || 0);
            if (revenueChange > spendChange && spendChange > 0) {
                insights.push({ type: 'success', text: 'Efficiency improving - revenue growing faster than spend' });
            }
        }

        return insights;
    };

    const insights = generateInsights();

    return (
        <tr>
            <td colSpan={14} style={{ padding: 0 }}>
                <div style={{
                    background: 'var(--color-bg-secondary)',
                    borderTop: '1px solid var(--color-border)',
                    borderBottom: '2px solid var(--color-primary)',
                    padding: 'var(--space-4) var(--space-6)'
                }}>
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                {[
                                    { id: 'trends', label: 'Daily Trends' },
                                    { id: 'comparison', label: 'Period Comparison' },
                                    { id: 'insights', label: 'Insights' },
                                    { id: 'creatives', label: 'Creatives', icon: ImageIcon }
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveTab(tab.id);
                                        }}
                                        style={{
                                            padding: 'var(--space-1) var(--space-3)',
                                            background: activeTab === tab.id ? 'var(--color-bg-primary)' : 'transparent',
                                            border: activeTab === tab.id ? '1px solid var(--color-border)' : '1px solid transparent',
                                            borderRadius: 'var(--radius-md)',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: activeTab === tab.id ? 600 : 400,
                                            color: activeTab === tab.id ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'
                                        }}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                            {periodInfo.previous && (
                                <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                                    <Clock size={12} />
                                    Comparing to {periodInfo.previous.start} - {periodInfo.previous.end}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onCollapse();
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-1) var(--space-2)',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--color-text-secondary)',
                                fontSize: 'var(--font-size-sm)'
                            }}
                        >
                            <ChevronUp size={16} />
                            Collapse
                        </button>
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 'var(--space-6)' }}>
                            <RefreshCw size={24} className="spinning" />
                            <p className="text-secondary" style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                                Loading trends from {campaign.platform}...
                            </p>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'trends' && (
                                <div>
                                    {dailyData.length === 0 ? (
                                        <div style={{
                                            textAlign: 'center',
                                            padding: 'var(--space-6)',
                                            color: 'var(--color-text-secondary)'
                                        }}>
                                            <BarChart3 size={32} style={{ marginBottom: 'var(--space-2)' }} />
                                            <p style={{ fontSize: 'var(--font-size-sm)' }}>
                                                No daily data available for this period
                                            </p>
                                        </div>
                                    ) : (
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(3, 1fr)',
                                            gap: 'var(--space-4)'
                                        }}>
                                            <TrendCard label="Daily Spend" dataKey="spend" format="currency" color="#ef4444" currentValue={dailyData[dailyData.length - 1]?.spend} />
                                            <TrendCard label="Daily Revenue" dataKey="revenue" format="currency" color="#22c55e" currentValue={dailyData[dailyData.length - 1]?.revenue} />
                                            <TrendCard label="Daily ROAS" dataKey="roas" format="roas" color="#3b82f6" currentValue={dailyData[dailyData.length - 1]?.roas} />
                                            <TrendCard label="Daily Conversions" dataKey="conversions" format="number" color="#f59e0b" currentValue={dailyData[dailyData.length - 1]?.conversions} />
                                            <TrendCard label="Cost per Click" dataKey="cpc" format="currency" color="#6b7280" currentValue={dailyData[dailyData.length - 1]?.cpc} />
                                            <TrendCard label="CPM" dataKey="cpm" format="currency" color="#8b5cf6" currentValue={dailyData[dailyData.length - 1]?.cpm} />
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'comparison' && (
                                <div>
                                    <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)' }}>
                                        Additional metrics not shown in the table - comparing current vs previous period
                                    </p>
                                    <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                                        <MetricCard icon={ShoppingCart} label="Conversions" value={formatNumber(current.conversions || 0)} change={changes.conversions} prevValue={previous.conversions ? formatNumber(previous.conversions) : null} />
                                        <MetricCard icon={DollarSign} label="Cost per Click" value={formatCurrency(current.cpc || 0)} change={changes.cpc} inverted={true} prevValue={previous.cpc ? formatCurrency(previous.cpc) : null} />
                                        <MetricCard icon={DollarSign} label="CPM" value={formatCurrency(current.cpm || 0)} change={changes.cpm} inverted={true} prevValue={previous.cpm ? formatCurrency(previous.cpm) : null} />
                                        <MetricCard icon={Target} label="Cost per Conversion" value={formatCurrency(current.costPerConversion || current.cpa || 0)} change={changes.costPerConversion} inverted={true} prevValue={previous.costPerConversion ? formatCurrency(previous.costPerConversion) : null} />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'insights' && (
                                <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                                    <div style={{ flex: 1 }}>
                                        {insights.length === 0 ? (
                                            <div style={{
                                                padding: 'var(--space-4)',
                                                background: 'var(--color-bg-primary)',
                                                borderRadius: 'var(--radius-md)',
                                                border: '1px solid var(--color-border)',
                                                color: 'var(--color-text-secondary)',
                                                fontSize: 'var(--font-size-sm)'
                                            }}>
                                                Monitoring performance. More insights will appear as data accumulates.
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                {insights.map((insight, index) => {
                                                    const colors = {
                                                        success: { bg: 'var(--color-success-bg)', border: 'var(--color-success)' },
                                                        warning: { bg: 'var(--color-warning-bg)', border: 'var(--color-warning)' },
                                                        danger: { bg: 'var(--color-error-bg)', border: 'var(--color-error)' }
                                                    };
                                                    const style = colors[insight.type] || colors.warning;
                                                    return (
                                                        <div key={index} style={{
                                                            padding: 'var(--space-2) var(--space-3)',
                                                            background: style.bg,
                                                            borderLeft: `3px solid ${style.border}`,
                                                            borderRadius: 'var(--radius-sm)',
                                                            fontSize: 'var(--font-size-sm)'
                                                        }}>
                                                            {insight.text}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{
                                        width: '200px',
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-bg-primary)',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border)'
                                    }}>
                                        <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-2)' }}>Overall Health</div>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            fontWeight: 600,
                                            fontSize: 'var(--font-size-sm)',
                                            color: current.roas >= 2 ? 'var(--color-success)' : current.roas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                                        }}>
                                            {current.roas >= 2 ? <><ArrowUpRight size={16} /> Performing Well</> : current.roas >= 1 ? <><Activity size={16} /> Needs Attention</> : <><ArrowDownRight size={16} /> Underperforming</>}
                                        </div>
                                        <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>Period Trend</div>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            fontWeight: 600,
                                            fontSize: 'var(--font-size-sm)',
                                            color: previous.roas && current.roas > previous.roas ? 'var(--color-success)' : previous.roas && current.roas < previous.roas ? 'var(--color-error)' : 'var(--color-text-secondary)'
                                        }}>
                                            {previous.roas ? (
                                                current.roas > previous.roas * 1.05 ? <><TrendingUp size={16} /> Improving</> :
                                                current.roas < previous.roas * 0.95 ? <><TrendingDown size={16} /> Declining</> :
                                                <><Minus size={16} /> Stable</>
                                            ) : <><Minus size={16} /> No prior data</>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'creatives' && (
                                <CreativesQuickView
                                    campaignId={campaign.platformCampaignId}
                                    platform={campaign.platform}
                                    userId={userId}
                                    brandId={brandId}
                                    dateRange={dateRange}
                                />
                            )}
                        </>
                    )}
                </div>

                {/* Zoomed Chart Modal */}
                {zoomedChart && (
                    <ChartModal
                        chart={zoomedChart}
                        onClose={() => setZoomedChart(null)}
                    />
                )}
            </td>
        </tr>
    );
};

export default CampaignDetailRow;
